/**
 * Music track playlists — the three background-music contexts, resolved to hashed
 * asset URLs by Vite (`?url`). The engine's `Music` player is playlist-agnostic; each
 * context (menu / play / editor) hands it the list it wants.
 *
 *   MENU   — the main-menu theme (single track, loops).
 *   PLAY   — playtest + community/campaign play (the two Speed Run tracks).
 *   EDITOR — the level editor (five tracks, one picked at random per session).
 *
 * `shuffled()` lives HERE (src/assets), NOT in src/engine/Music.ts, on purpose: track
 * order is background flavour, never part of the deterministic sim, and the engine
 * determinism lint (Math.random ban) only covers src/engine — so the random pick stays
 * outside that boundary and the sim's replay parity is untouched.
 */
import menu from './speedrun_menu.ogg?url';
import play1 from './speedrun_play_1.ogg?url';
import play2 from './speedrun_play_2.ogg?url';
import ed1 from './editor_music_1.ogg?url';
import ed2 from './editor_music_2.ogg?url';
import ed3 from './editor_music_3.ogg?url';
import ed4 from './editor_music_4.ogg?url';
import ed5 from './editor_music_5.ogg?url';

/** Main-menu theme — one track, looped. */
export const MENU_TRACKS: readonly string[] = [menu];

/** Play context (playtest + real level play) — the two Speed Run tracks alternate. */
export const PLAY_TRACKS: readonly string[] = [play1, play2];

/** Level editor — five tracks; a random order is chosen per editor session. */
export const EDITOR_TRACKS: readonly string[] = [ed1, ed2, ed3, ed4, ed5];

/**
 * Fresh Fisher–Yates shuffle of a track list. NON-deterministic on purpose (music
 * order must vary between sessions); safe because this module is outside src/engine,
 * so Math.random here never touches the sim's seeded RNG or replay parity.
 */
export function shuffled(tracks: readonly string[]): string[] {
  const a = tracks.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
